"use client";

import { useMemo } from "react";
import { ComparisonTable } from "@/components/optimizer/comparison-table";
import { displayPlayerPropToComparisonData } from "@/components/optimizer/comparison-table-types";
import { rowWhalesSpec } from "@/components/optimizer/utils-v2/displayed-odds";
import { WhalesCard } from "@/components/odds/whales";
import { useWhalesSlot } from "@/hooks/use-whales-gate";
import { OptimizerExpandedHistory } from "@/components/optimizer/optimizer-expanded-history";
import type { DisplayPlayerProp, PlatformType } from "@/components/optimizer/utils-v2";
import type { OpenOddsHistory } from "@/components/odds/odds-history-selection";
import type { SportsbookId } from "@/lib/sportsbook-registry";
import { trackBetLinkClick } from "@/utils/analytics";
import type { OddsFormat } from "@/utils/calculators/odds-utils";
import { columnBooks, type BoardColumn } from "./model";

export function trackPropBetLink(row: DisplayPlayerProp, sportsbook: string, source: "cta" | "peer_grid"): void {
  trackBetLinkClick({
    sportsbook,
    page: "optimizer",
    bet_type: row.market_type === "game" ? "gameline" : "prop",
    ev_percentage: Number(row.ev_percentage) || undefined,
    league: (row.league ?? row.sport ?? undefined) as string | undefined,
    click_source: source,
  });
}

export function FantasyExpansion({
  row, platform, columns, format, onOpenHistory, oddsScreenHref, isMobile,
}: {
  row: DisplayPlayerProp;
  platform: PlatformType;
  columns: readonly BoardColumn[];
  format: OddsFormat;
  onOpenHistory: OpenOddsHistory;
  oddsScreenHref: string | null;
  isMobile: boolean;
}) {
  const books = useMemo(() => [platform, ...columnBooks(columns).filter((book) => book !== platform)] as SportsbookId[], [platform, columns]);
  const data = useMemo(
    () => displayPlayerPropToComparisonData(row, books, platform as SportsbookId, isMobile),
    [row, books, platform, isMobile],
  );
  const whalesSlot = useWhalesSlot(row);
  const whalesAt = useMemo(() => {
    const at = columns.findIndex((column) => column.kind === "whales");
    if (at < 0 || !whalesSlot) return null;
    const before = new Set<string>([platform, ...columnBooks(columns.slice(0, at))]);
    return data.books.filter((book) => before.has(book.book)).length;
  }, [columns, data.books, platform, whalesSlot]);
  return (
    <div className="border-l-2 border-l-primary bg-surface-raised">
      <OptimizerExpandedHistory row={row} books={data.books} openHistory={onOpenHistory}>
        {({ renderHistoryTrigger, renderMovementDelta }) => (
          <ComparisonTable
            data={data}
            oddsFormat={format}
            renderHistoryTrigger={renderHistoryTrigger}
            renderMovementDelta={renderMovementDelta}
            oddsScreenHref={oddsScreenHref}
            inserted={whalesAt === null ? null : { at: whalesAt, node: <WhalesCard side={rowWhalesSpec(row, "side")} opp={data.singleSideOnly ? null : rowWhalesSpec(row, "opp")} format={format} tap={isMobile} /> }}
            onBookLinkClick={(book) => trackPropBetLink(row, book, "peer_grid")}
          />
        )}
      </OptimizerExpandedHistory>
    </div>
  );
}
